import { debounce } from "throttle-debounce";
import {
  APIModule,
  CopyModulePost,
  ModulePatch,
} from "../../../server/types/index.js";
import { arePageModulesLoaded } from "../../selectors/modules.js";
import {
  ChangeModuleSet,
  CreateModulePostData,
  Language,
  ModuleSettingsTypes,
  ModuleTranslations,
  MoveModulePostData,
  PartialModuleSettings,
  PostModuleParams,
  PostMoveModuleParams,
  StoreState,
  ThunkAction,
  ThunkDispatch,
} from "../../types/index.js";
import {
  actionModuleTypes,
  copiedModuleClipboard,
  fetch,
  getNextModuleId,
  isDeletableModule,
  isItemUntranslated,
  isWretchError,
  ModuleNotFoundError,
} from "../../utils/utils.js";
import { showAlert } from "../alerts.js";
import { createAppAsyncThunk } from "../utils.js";
import {
  changeModuleStart,
  deleteModuleTranslationError,
  deleteModuleTranslationStart,
  moveModuleStart,
  setModuleSettingsStart,
} from "./actions.js";
import { actionPrefix } from "./constants.js";

export const getActionModules = createAppAsyncThunk(
  `${actionPrefix}/getActionModules`,
  async (siteId: string, { dispatch }) => {
    const data = await fetch({ dispatch })
      .query({ "type[]": actionModuleTypes })
      .get(`sites/${siteId}/modules`)
      .json<APIModule[]>();
    return data;
  },
  {
    condition: (_siteId, { getState }) =>
      getState().loadStates.actionModules === "unloaded",
  },
);

export const getSiteModules = createAppAsyncThunk(
  `${actionPrefix}/getSiteModules`,
  async (siteId: string, { dispatch }) => {
    try {
      const data = await fetch({ dispatch })
        .query({ global: true })
        .get(`sites/${siteId}/modules`)
        .json<APIModule[]>();
      return data;
    } catch (error) {
      dispatch(
        showAlert("Beim Laden der globalen Module ist ein Fehler aufgetreten!"),
      );
      throw error;
    }
  },
  {
    condition: (_siteId, { getState }) =>
      getState().loadStates.siteModules === "unloaded",
  },
);

export const getModules = createAppAsyncThunk(
  `${actionPrefix}/getModules`,
  async (
    { siteId, pageId }: { siteId: string; pageId: string; forceLoad: boolean },
    { dispatch },
  ) => {
    try {
      const data = await fetch({ dispatch })
        .get(`sites/${siteId}/pages/${pageId}/modules`)
        .json<APIModule[]>();
      return data;
    } catch (error) {
      dispatch(showAlert("Beim Laden der Module ist ein Fehler aufgetreten!"));
      throw error;
    }
  },
  {
    condition: ({ pageId, forceLoad }, { getState }) =>
      forceLoad || !arePageModulesLoaded(getState().modules.byPageId, pageId),
  },
);

export const postModule = createAppAsyncThunk(
  `${actionPrefix}/postModule`,
  async (params: PostModuleParams, { dispatch, getState }) => {
    const module = getState().modules.byId[params.moduleId];
    if (!module) throw new ModuleNotFoundError(params.moduleId);

    const {
      siteId,
      parentId,
      type: moduleType,
      id: moduleId,
      ...rest
    } = module;

    const postData: CreateModulePostData = {
      ...rest,
      moduleType,
      next: params.next || null,
      parentId,
    };

    try {
      const data = await fetch({ dispatch })
        .post(postData, `sites/${siteId}/modules/${moduleId}`)
        .json<APIModule | undefined>();
      return data;
    } catch (error) {
      dispatch(
        showAlert("Beim Speichern des Moduls ist ein Fehler aufgetreten!"),
      );
      throw error;
    }
  },
);

// TODO: debounced logic for api calls (in optimistic updates) is not ideal for createAsyncThunk as we can't move the debounce into the ui, hence we keep it as a normal thunk action for now, but it would be good to find a better solution for this in the future
// For example create an optimistic update action that only updates the store and a listener middleware (https://redux-toolkit.js.org/api/createListenerMiddleware) for this action that triggers the debounced api call and handles potential rollback
export const changeModule =
  (
    siteId: string,
    moduleId: string,
    changeset: ChangeModuleSet,
  ): ThunkAction<void> =>
  (dispatch, getState) => {
    dispatch(changeModuleStart({ moduleId, changeset }));

    debouncedPatchModuleRequest({
      dispatch,
      getState,
      moduleId,
      patch: changeset,
      siteId,
    });
  };

export const translateModule = createAppAsyncThunk(
  `${actionPrefix}/translateModule`,
  async (
    {
      siteId,
      moduleId,
      languageId,
    }: {
      siteId: string;
      moduleId: string;
      pageId: string | null;
      languageId: Language;
      sourceLanguageId: Language;
    },
    { dispatch, getState },
  ) => {
    const translations: ModuleTranslations = {};
    translations[languageId] =
      getState().modules.byId[moduleId]?.translations[languageId];

    patchModuleRequest({
      dispatch,
      getState,
      moduleId,
      patch: { translations },
      siteId,
    });
  },
);

// TODO: debounced logic for api calls (in optimistic updates) is not ideal for createAsyncThunk as we can't move the debounce into the ui, hence we keep it as a normal thunk action for now, but it would be good to find a better solution for this in the future
// For example create an optimistic update action that only updates the store and a listener middleware (https://redux-toolkit.js.org/api/createListenerMiddleware) for this action that triggers the debounced api call and handles potential rollback
export const moveModule = (
  siteId: string,
  pageId: string,
  moduleId: string,
  moveBy: number,
): ThunkAction<void> => {
  return (dispatch, getState) => {
    const module = getState().modules.byId[moduleId];
    if (!module?.pageId) return;

    dispatch(moveModuleStart({ pageId: module.pageId, moduleId, moveBy }));

    const params: PostMoveModuleParams = {
      siteId,
      pageId,
      moduleId,
      hasMovedBy: moveBy,
    };

    debouncedMoveModule(dispatch, params);
  };
};

export const postMoveModule = createAppAsyncThunk(
  `${actionPrefix}/postMoveModule`,
  async (
    { siteId, pageId, moduleId, hasMovedBy }: PostMoveModuleParams,
    { dispatch, getState },
  ) => {
    const { modules } = getState();
    const module = getState().modules.byId[moduleId];
    if (!module) throw new ModuleNotFoundError(moduleId);
    const { parentId } = module;

    const nextModuleId = getNextModuleId(moduleId, modules);

    const postData: MoveModulePostData = {
      next: nextModuleId || null,
      parentId,
    };

    try {
      await fetch({ dispatch })
        .post(postData, `sites/${siteId}/modules/${moduleId}/move`)
        .res();
    } catch (error) {
      // If an error occurred, reset the position to before the move
      dispatch(moveModuleStart({ pageId, moduleId, moveBy: -hasMovedBy }));

      dispatch(
        showAlert("Beim Verschieben des Moduls ist ein Fehler aufgetreten!"),
      );
      throw error;
    }
  },
  {
    dispatchConditionRejection: true,
    condition: ({ moduleId }, { getState }) =>
      !!getState().modules.byId[moduleId],
  },
);

export const copyModule = createAppAsyncThunk(
  `${actionPrefix}/copyModule`,
  async (
    {
      siteId,
      targetPageId,
      moduleId,
      targetModuleId,
      newModuleShortId,
    }: {
      siteId: string;
      moduleId: string;
      targetPageId: string | null;
      targetModuleId: string | null;
      newModuleShortId: string;
    },
    { dispatch, requestId },
  ) => {
    try {
      const postData: CopyModulePost = {
        targetModuleId,
        targetPageId,
        newModuleShortId,
      };

      const data = await fetch({ dispatch })
        .post(postData, `sites/${siteId}/modules/${moduleId}/copy`)
        .json<APIModule[]>();

      dispatch(
        targetPageId !== null
          ? getModules.fulfilled(data, requestId, {
              siteId,
              pageId: targetPageId,
              forceLoad: false,
            })
          : getSiteModules.fulfilled(data, requestId, siteId),
      );

      return data;
    } catch (error) {
      const responseStatus = isWretchError(error) ? error.status : undefined;

      const errorMessage =
        responseStatus === 404
          ? "Das kopierte Modul wurde gelöscht und kann daher nicht hier eingefügt werden!"
          : "Beim Einfügen Moduls ist ein Fehler aufgetreten!";

      dispatch(showAlert(errorMessage));

      // Clear copied module if the module was removed
      [404, 422, 400].indexOf(responseStatus ?? 0) !== -1 &&
        copiedModuleClipboard.remove(siteId);

      throw error;
    }
  },
);

export const setModuleSetting = <Settings extends ModuleSettingsTypes>(
  {
    id,
    siteId,
    translation: { languageId },
  }: { id: string; siteId: string; translation: { languageId: Language } },
  settings: PartialModuleSettings<Settings>,
) => {
  return setModuleSettings<Settings>(siteId, languageId, id, settings);
};

// TODO: debounced logic for api calls (in optimistic updates) is not ideal for createAsyncThunk as we can't move the debounce into the ui, hence we keep it as a normal thunk action for now, but it would be good to find a better solution for this in the future
// For example create an optimistic update action that only updates the store and a listener middleware (https://redux-toolkit.js.org/api/createListenerMiddleware) for this action that triggers the debounced api call and handles potential rollback
/**
 * Set the module settings for a specific module translation. The passed settings
 * will extend (but not deep extend) the existing settings, so it’s not necessary to pass
 * all top level properties of the settings.
 */
export const setModuleSettings =
  <Settings extends ModuleSettingsTypes = ModuleSettingsTypes>(
    siteId: string,
    languageId: Language,
    moduleId: string,
    settings: PartialModuleSettings<Settings>,
  ): ThunkAction<void> =>
  (dispatch, getState) => {
    dispatch(setModuleSettingsStart({ moduleId, languageId, settings }));

    const currentModule = getState().modules.byId[moduleId];
    const translation = currentModule?.translations[languageId];

    if (!translation) {
      throw new Error(
        `Translation for in ${languageId} for module ${moduleId} not found`,
      );
    }

    const translations: ModuleTranslations = {};
    translations[languageId] = { settings: translation.settings };

    debouncedPatchModuleRequest({
      dispatch,
      getState,
      moduleId,
      patch: { translations, settings: currentModule.settings },
      siteId,
    });
  };

// TODO: Rewrite to createAsyncThunk with optimistic updates logic (prevStates)
// But for now keep as normal thunk action because of the complexity in the modules state with sub reducers and the internal and recursive use in the reducers
export const deleteModuleTranslation =
  ({
    siteId,
    pageId,
    languageId,
    moduleId,
    deleteAllTranslations = false,
  }: {
    siteId: string;
    pageId: string | null;
    languageId: Language;
    moduleId: string;
    deleteAllTranslations: boolean;
  }): ThunkAction<void> =>
  async (dispatch, getState) => {
    const { modules } = getState();
    const previousModules = modules;
    const currentModule = modules.byId[moduleId];
    if (!currentModule) throw new ModuleNotFoundError(moduleId);
    const { parentId, type } = currentModule;
    const _deleteAllTranslations =
      deleteAllTranslations || isItemUntranslated(currentModule);
    if (!isDeletableModule(type)) return undefined;
    dispatch(
      deleteModuleTranslationStart({
        pageId,
        languageId,
        moduleId,
        moduleType: type,
        parentId,
        deleteAllTranslations: _deleteAllTranslations,
      }),
    );
    const url = `sites/${siteId}/modules/${moduleId}`;
    const patchBody: ModulePatch = { translations: { [languageId]: null } };

    try {
      return await (_deleteAllTranslations
        ? fetch({ dispatch }).delete(url).res()
        : fetch({ dispatch }).patch(patchBody, url).res());
    } catch (error) {
      dispatch(deleteModuleTranslationError({ prevState: previousModules }));
      dispatch(
        showAlert("Beim Löschen des Moduls ist ein Fehler aufgetreten!"),
      );
      throw error;
    }
  };

/**
 * Get the debounce function on a per-module basis
 */
const debouncedMoveModuleCreator = () => {
  const memoizedFunctions: {
    [moduleId: string]: (
      dispatch: ThunkDispatch,
      params: PostMoveModuleParams,
    ) => void;
  } = {};

  return (dispatch: ThunkDispatch, params: PostMoveModuleParams) => {
    const { moduleId } = params;

    memoizedFunctions[moduleId] =
      memoizedFunctions[moduleId] ||
      // Only 500 ms, a higher value could lead to mismatches
      // between server and client if an user moves modules fast
      debounce(500, (dispatch: ThunkDispatch, params: PostMoveModuleParams) =>
        dispatch(postMoveModule(params)),
      );

    memoizedFunctions[moduleId]?.(dispatch, params);
  };
};

const debouncedMoveModule = debouncedMoveModuleCreator();

const patchModuleRequest = async ({
  dispatch,
  getState,
  siteId,
  moduleId,
  patch,
}: {
  dispatch: ThunkDispatch;
  getState: () => StoreState;
  siteId: string;
  moduleId: string;
  patch: ModulePatch;
}) => {
  const currentModule = getState().modules.byId[moduleId];

  // If the module no longer exists (e.g. it was deleted), abort
  if (!currentModule) return;

  try {
    const data = await fetch({ dispatch })
      .patch(patch, `sites/${siteId}/modules/${moduleId}`)
      .json<APIModule>();

    return data;
  } catch (error) {
    dispatch(
      showAlert("Beim Aktualisieren des Moduls ist ein Fehler aufgetreten!"),
    );
    throw error;
  }
};

export const debouncedPatchModuleRequest = debounce(2000, patchModuleRequest);
